import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getLastOpposition } from '../utils/lastOpposition';
import './ArticlePage.css';

// Los nombres de ley (p.ej. "Ley 1/2004") pueden contener "/", lo que rompe el
// path-matching del backend si van como segmento de URL. Se envían como query params,
// así que aquí nos aseguramos de tener el valor "plano" (sin codificar) sea cual sea
// el estado en que useParams() nos lo entregue.
const safeDecode = (s) => {
  try { return decodeURIComponent(s); } catch { return s; }
};

// El texto de los artículos trae saltos de línea "de word-wrap" heredados del BOE
// que no son fin de párrafo real (p.ej. cortan a mitad de frase o antes de un
// paréntesis suelto). Solo cerramos párrafo cuando lo acumulado hasta ahora
// termina en puntuación de cierre de frase; si no, la siguiente línea es
// continuación de la misma frase.
const splitIntoParagraphs = (text) => {
  const lines = (text || '').split('\n').map((l) => l.trim()).filter(Boolean);
  const paragraphs = [];
  let buffer = '';
  for (const line of lines) {
    if (!buffer) {
      buffer = line;
    } else if (/[.:;»"]$/.test(buffer)) {
      paragraphs.push(buffer);
      buffer = line;
    } else {
      buffer += ' ' + line;
    }
  }
  if (buffer) paragraphs.push(buffer);
  return paragraphs;
};

const ArticlePage = () => {
  const { law_name, article_number } = useParams();
  const lawName = safeDecode(law_name);
  const articleNumber = safeDecode(article_number);
  const [article, setArticle] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [stats, setStats] = useState(null);
  const [alsoRead, setAlsoRead] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const params = { law_name: lawName, article_number: articleNumber };
        const [art, sim, st, ar] = await Promise.all([
          axios.get('/api/article_content', { params }),
          axios.get('/api/article/similar', { params }).catch(() => ({ data: [] })),
          axios.get('/api/article/stats', { params: { ...params, opposition: getLastOpposition() || undefined } }).catch(() => ({ data: {} })),
          axios.get('/api/article/also-read', { params }).catch(() => ({ data: [] })),
        ]);
        setArticle(art.data);
        setSimilar(sim.data || []);
        setStats(st.data || {});
        setAlsoRead(ar.data || []);
        // Tracking: registra lectura
        axios.post('/api/track/read', { law: lawName, number: articleNumber })
          .catch(() => {});
      } catch (error) {
        console.error('Error fetching article', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [lawName, articleNumber]);

  if (loading) return <div className="page-loader">Cargando artículo…</div>;

  if (!article) {
    return (
      <div>
        <button onClick={() => navigate(-1)} className="back-link">
          ← Volver
        </button>
        <div className="empty-state">
          <p>Artículo no encontrado.</p>
        </div>
      </div>
    );
  }

  // Las veces que ha caído van separadas por oposición: no se mezclan Auxilio y Tramitación.
  const askedByOpposition = ((stats && stats.by_opposition) || []).filter((o) => o.times_asked > 0);

  return (
    <div className="article-page">
      <button onClick={() => navigate(-1)} className="back-link">
        ← Volver
      </button>

      <header className="page-header">
        <h1 className="page-title">{articleNumber}</h1>
        <p className="page-subtitle">{article.law}</p>
        {stats && (askedByOpposition.length > 0 || stats.users_read > 0) && (
          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 12, color: '#666', flexWrap: 'wrap' }}>
            {askedByOpposition.map((o) => (
              <span key={o.opposition} style={{ background: '#fff4d9', color: '#b07700', padding: '3px 10px', borderRadius: 12, fontWeight: 600 }}>
                📋 Ha caído {o.times_asked} {o.times_asked === 1 ? 'vez' : 'veces'} en exámenes de {o.opposition}
              </span>
            ))}
            {stats.users_read > 1 && (
              <span style={{ background: '#e8f3fb', color: '#0067b8', padding: '3px 10px', borderRadius: 12 }}>
                👥 {stats.users_read} usuarios lo han leído
              </span>
            )}
            {stats.pagerank > 0.0003 && (
              <span style={{ background: '#e8f5e9', color: '#1b5e20', padding: '3px 10px', borderRadius: 12, fontWeight: 600 }}>
                ⭐ Artículo clave
              </span>
            )}
          </div>
        )}
      </header>

      {similar.length > 0 && (
        <button
          onClick={() => navigate(`/article/${encodeURIComponent(similar[0].law)}/${encodeURIComponent(similar[0].number)}`)}
          className="btn btn-secondary"
          style={{ marginBottom: 12 }}
        >
          Siguiente artículo relacionado: {similar[0].number} →
        </button>
      )}

      {article.summary && (
        <div className="card article-summary">
          <h3 className="article-section-title">Resumen</h3>
          <p>{article.summary}</p>
        </div>
      )}

      <div className="card article-text-card">
        <h3 className="article-section-title">Texto completo</h3>
        <div className="article-text">
          {splitIntoParagraphs(article.text).map((line, i) => (
            <p key={i}>{line}</p>
          ))}
        </div>
      </div>

      {alsoRead.length > 0 && (
        <div className="card">
          <h3 className="article-section-title">👥 Otros opositores también leyeron</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {alsoRead.map((a, i) => (
              <a
                key={i}
                href={`/article/${encodeURIComponent(a.law)}/${encodeURIComponent(a.number)}`}
                className="list-item"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/article/${encodeURIComponent(a.law)}/${encodeURIComponent(a.number)}`);
                }}
              >
                <div>
                  <div className="list-item-title">{a.number}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {a.law} · {a.shared_users} usuarios
                  </div>
                </div>
                <span className="list-item-arrow">→</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {similar.length > 0 && (
        <div className="card">
          <h3 className="article-section-title">Artículos relacionados</h3>
          <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px' }}>
            Tratan conceptos similares (similitud Jaccard)
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {similar.map((s, i) => (
              <a
                key={i}
                href={`/article/${encodeURIComponent(s.law)}/${encodeURIComponent(s.number)}`}
                className="list-item"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/article/${encodeURIComponent(s.law)}/${encodeURIComponent(s.number)}`);
                }}
              >
                <div>
                  <div className="list-item-title">{s.number}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {s.law} · score {(s.score * 100).toFixed(0)}%
                  </div>
                </div>
                <span className="list-item-arrow">→</span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlePage;
